<template>
  <div>
  <div class="contact-form-headline header-container">
    <h3>Der erste Schritt ist geschafft!</h3>
    <span>Für die weitere Prüfung und individuelle Beratung benötigen wir nur noch Ihre Kontaktdaten.</span>
</div>
  <div class="contact-form">
    <div
        v-if="globalApiError"
        class="global-error-banner"
        role="alert"
        aria-live="assertive"
    >
      <p class="global-error-text">{{ globalApiError }}</p>
    </div>
    <form @submit.prevent="handleSubmit" novalidate :class="{ 'is-loading': workflowStore.isLoading }">
      <div v-if="workflowStore.isLoading" class="form-loading-overlay">
        <div class="spinner-container">
          <div class="spinner"></div>
          <p id="loading-message" tabindex="-1">Daten werden gesendet...</p>
        </div>
      </div>
      <div class="form-group" role="radiogroup" aria-labelledby="salutation-label" :aria-invalid="!!formErrors.salutation" aria-errormessage="salutation-error">
        <fieldset>
          <legend id="salutation-label"><strong>Anrede</strong></legend>
        <div class="radio-options">
          <label v-for="option in salutationOptions" :key="option.value" :for="'salutation_' + option.value">
            <input
                type="radio"
                v-model="formData.salutation"
                :value="option.value"
                :id="'salutation_' + option.value"
                name="salutation"
                aria-required="true"
                autocomplete="honorific-prefix" @change="validateField('salutation'); handleInputChange('salutation')"
            />
            {{ option.label }}
          </label>
        </div>
        <p v-if="formErrors.salutation" :id="'salutation-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.salutation }}</p>
        </fieldset>
      </div>

      <fieldset>
        <legend><strong>Kontaktdaten</strong></legend>

        <!-- Vorname und Nachname -->
        <div class="form-row">
      <div class="form-group">
        <label for="firstName" class="form-label-notch">Vorname</label>
        <input
            type="text"
            id="firstName"
            v-model.trim="formData.firstName"
            aria-required="true"
            :aria-invalid="!!formErrors.firstName"
            aria-errormessage="firstName-error"
            autocomplete="given-name" @blur="validateField('firstName')" @input="handleInputChange('firstName')"
        />
        <p v-if="formErrors.firstName" :id="'firstName-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.firstName }}</p>
      </div>

      <div class="form-group">
        <label for="lastName" class="form-label-notch">Nachname</label>
        <input
            type="text"
            id="lastName"
            v-model.trim="formData.lastName"
            aria-required="true"
            :aria-invalid="!!formErrors.lastName"
            aria-errormessage="lastName-error"
            autocomplete="family-name" @blur="validateField('lastName')" @input="handleInputChange('lastName')"
        />
        <p v-if="formErrors.lastName" :id="'lastName-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.lastName }}</p>
      </div>
  </div>
        <!-- Mailadresse -->
      <div class="form-group">
        <label for="email" class="form-label-notch">E-Mail-Adresse</label>
        <input
            type="email"
            id="email"
            v-model.trim="formData.email"
            aria-required="true"
            :aria-invalid="!!formErrors.email"
            aria-errormessage="email-error"
            autocomplete="email" @blur="validateField('email')" @input="handleInputChange('email')"
        />
        <p v-if="formErrors.email" :id="'email-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.email }}</p>
      </div>

        <!-- Telefon und Rabattcode -->
        <div class="form-row">
      <div class="form-group">
        <label for="phone" class="form-label-notch">Telefon</label>

        <input
            type="tel"
            id="phone"
            v-model.trim="formData.phone"
            aria-required="true"
            :aria-invalid="!!formErrors.phone || getApiError('phone')"
            aria-errormessage="phone-error"
            inputmode="numeric"
            autocomplete="tel" @input="handleInputChange('phone')" @blur="validateField('phone')"
        />
        <p v-if="formErrors.phone || getApiError('phone')" :id="'phone-error'" class="error-message" role="alert" aria-live="polite">{{  getCombinedFieldError('phone') }}</p>
      </div>
          <div class="form-group">
          <label for="rabattcode" class="form-label-notch">Rabattcode (optional)</label>
          <input
              type="text"
              id="rabattcode"
              v-model.trim="formData.rabattcode"
              aria-required=false
          />
            </div>
        </div>
      </fieldset>



      <template v-if="showSecondContact">
        <hr>
      <div class="form-group checkbox-group">
        <input
            type="checkbox"
            id="diffAddress"
            v-model="formData.differentPostalAddress"
        />
        <label for="diffAddress">Abweichende Postanschrift (optional)</label>
      </div>

      <div v-if="formData.differentPostalAddress" class="different-address-section">
        <fieldset>
          <legend><strong>Abweichende Postanschrift</strong></legend>

          <div class="form-group" role="radiogroup" aria-labelledby="post-salutation-label" :aria-invalid="!!formErrors.postalAddress.salutation" aria-errormessage="post-salutation-error">
            <fieldset>
              <legend id="post-salutation-label"><strong>Anrede</strong></legend>
              <div class="radio-options">
                <label v-for="option in salutationOptions" :key="option.value" :for="'post_salutation_' + option.value">
                  <input
                      type="radio"
                      v-model="formData.postalAddress.salutation"
                      :value="option.value"
                      :id="'post_salutation_' + option.value"
                      name="post_salutation"
                      aria-required="true"
                      @change="validateField('postalAddress.salutation'); handleInputChange('postalAddress.salutation')"
                  />
                  {{ option.label }}
                </label>
              </div>
              <p v-if="formErrors.postalAddress.salutation" :id="'post-salutation-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.postalAddress.salutation }}</p>
            </fieldset>
          </div>
          <div class="form-group">
            <label for="postFirstName" class="form-label-notch">Vorname</label>
            <input
                type="text"
                id="postFirstName"
                v-model.trim="formData.postalAddress.firstName"
                aria-required="true"
                :aria-invalid="!!formErrors.postalAddress.firstName"
                aria-errormessage="postFirstName-error"
                @blur="validateField('postalAddress.firstName')" @input="handleInputChange('postalAddress.firstName')"
            />
            <p v-if="formErrors.postalAddress.firstName" :id="'postFirstName-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.postalAddress.firstName }}</p>
          </div>

          <div class="form-group">
            <label for="postLastName" class="form-label-notch">Nachname</label>
            <input
                type="text"
                id="postLastName"
                v-model.trim="formData.postalAddress.lastName"
                aria-required="true"
                :aria-invalid="!!formErrors.postalAddress.lastName"
                aria-errormessage="postLastName-error"
                @blur="validateField('postalAddress.lastName')" @input="handleInputChange('postalAddress.lastName')"
            />
            <p v-if="formErrors.postalAddress.lastName" :id="'postLastName-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.postalAddress.lastName }}</p>
          </div>

          <div class="form-row">
            <div class="form-group street-group">
              <label for="postStreet" class="form-label-notch">Straße</label>
              <input
                  type="text"
                  id="postStreet"
                  v-model.trim="formData.postalAddress.street"
                  aria-required="true"
                  :aria-invalid="!!formErrors.postalAddress.street"
                  aria-errormessage="postStreet-error"
                  @blur="validateField('postalAddress.street')" @input="handleInputChange('postalAddress.street')"
              />
              <p v-if="formErrors.postalAddress.street" :id="'postStreet-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.postalAddress.street }}</p>
            </div>

            <div class="form-group house-number-group">
              <label for="postHouseNumber" class="form-label-notch">Hausnummer</label>
              <input
                  type="text"
                  id="postHouseNumber"
                  v-model.trim="formData.postalAddress.houseNumber"
                  aria-required="true"
                  :aria-invalid="!!formErrors.postalAddress.houseNumber"
                  aria-errormessage="postHouseNumber-error"
                  @blur="validateField('postalAddress.houseNumber')" @input="handleInputChange('postalAddress.houseNumber')"
              />
              <p v-if="formErrors.postalAddress.houseNumber" :id="'postHouseNumber-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.postalAddress.houseNumber }}</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group zip-code-group">
              <label for="postZipCode" class="form-label-notch">PLZ</label>
              <input
                  type="text"
                  id="postZipCode"
                  v-model.trim="formData.postalAddress.zipCode"
                  aria-required="true"
                  :aria-invalid="!!formErrors.postalAddress.zipCode"
                  aria-errormessage="postZipCode-error"
                  pattern="^\d{5}$"
                  maxlength="5"
                  inputmode="numeric"
                  @blur="validateField('postalAddress.zipCode')" @input="handleInputChange('postalAddress.zipCode')"
              />
              <p v-if="formErrors.postalAddress.zipCode"  :id="'postZipCode-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.postalAddress.zipCode }}</p>
              <p v-else-if="getApiError('zipCode')" :id="'postZipCode-error'" class="error-message" role="alert" aria-live="polite">{{getApiError('zipCode')}}</p>
            </div>

            <div class="form-group city-group">
              <label for="postCity" class="form-label-notch">Ort</label>
              <input
                  type="text"
                  id="postCity"
                  v-model.trim="formData.postalAddress.city"
                  aria-required="true"
                  :aria-invalid="!!formErrors.postalAddress.city"
                  aria-errormessage="postCity-error"
                  @blur="validateField('postalAddress.city')" @input="handleInputChange('postalAddress.city')"
              />
              <p v-if="formErrors.postalAddress.city" :id="'postCity-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.postalAddress.city }}</p>
            </div>
          </div>

        </fieldset>
      </div>


      <hr>
      </template>

      <div class="form-group checkbox-group terms-checkbox">
        <input
            type="checkbox"
            id="termsAccepted"
            v-model="formData.termsAccepted"
            aria-required="true"
            :aria-invalid="!!formErrors.termsAccepted"
            aria-errormessage="termsAccepted-error"
            @change="validateField('termsAccepted')"
        />
        <label for="termsAccepted">
          <strong>Zustimmung zu den Beratungsbedingungen:</strong><br />
          Ich habe die <a :href="pdfLink" target="_blank" rel="noopener noreferrer">Beratungsbedingungen</a>
          der Vattenfall Next Energy GmbH zur Kenntnis genommen und bin mit der Geltung einverstanden.
          Weitere Informationen zur Datenverarbeitung der Vattenfall Next Energy GmbH finden Sie in den <a href="https://www.vattenfall.de/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzinformationen</a>.
        </label>
        <p v-if="formErrors.termsAccepted" :id="'termsAccepted-error'" class="error-message" role="alert" aria-live="polite">{{ formErrors.termsAccepted }}</p>
      </div>

      <div v-if="hasValidVesOptinText" class="form-group checkbox-group terms-checkbox ves-optin">
        <input
            type="checkbox"
            id="vesOptin"
            v-model="formData.vesOptin"
        />
        <label for="vesOptin" v-html="optinTextVES">

        </label>
      </div>

      <div v-if="showOptinVES" class="form-group checkbox-group terms-checkbox ves-optin">
        <input type="checkbox" id="vnx_optin" v-model="formData.vnxOptin" />
        <label for="vnx_optin">
          <strong>Weitere Aktionen & Vorteile zu meinem Angebot (optional):</strong><br />
          Ich bin damit einverstanden, dass mich die Vattenfall Next Energy GmbH per E-Mail zu Kundenbefragungen sowie zu Aktionen und Vorteilen zu meinem Energielösungs-Angebot informiert. Meine Einwilligung ist freiwillig und für die Vertragserfüllung nicht erforderlich. Sie gilt bis auf Widerruf und kann jederzeit mit Wirkung für die Zukunft per <a href="mailto:vnx-werbewiderspruch@vattenfall.de">E-Mail</a> widerrufen werden. Weitere Informationen finde ich in den <a href="https://www.vattenfall.de/datenschutz#solutions" target="_blank" rel="noopener noreferrer">Datenschutzhinweisen</a>.
        </label>
      </div>
<hr>
      <div class="form-actions">
        <button type="button" class="button_link_back" @click="workflowStore.sendWorkflowEvent({ type: 'BACK' })">
          Zurück
      </button>
        <button type="submit" class="button button_yellow">Absenden</button>
      </div>
    </form>
  </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, computed, onMounted } from 'vue';
import { useWorkflowStore } from '../../stores/workflow.js';
import {validationRules} from "@/utils/contactFormValidation";
const pdfLink = `${process.env.BASE_URL}Beratungsbedingungen.pdf`;

const workflowStore = useWorkflowStore();

const showSecondContact = computed(() => {
  return process.env.VUE_APP_SECOND_CONTACT === 'true';
});

const showOptinVES = computed(() => {
  return process.env.VUE_APP_VES_OPTIN === 'true';
});

const optinTextVESUrl = computed(() => {
  return process.env.VUE_APP_OPTIN_TEXT_URL || '';
});

const optinTextVES = ref('');

const MIN_OPTIN_LENGTH = 10;

const hasValidVesOptinText = computed(() => {
  return typeof optinTextVES.value === 'string' &&
      optinTextVES.value.trim().length >= MIN_OPTIN_LENGTH;
});

const formData = ref({
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  houseNumber: '',
  zipCode: workflowStore.workflowContext.formData?.contactForm?.zipCode || workflowStore.workflowContext.formData?.building?.zip || '',
  city: '',
  differentPostalAddress: false,
  postalAddress: {
    salutation: '',
    firstName: '',
    lastName: '',
    street: '',
    houseNumber: '',
    zipCode: '',
    city: '',
  },
  termsAccepted: false,
  vnxOptin: false,
  vesOptin: false,
});

const salutationOptions = [
  { label: 'Herr', value: 'Herr' },
  { label: 'Frau', value: 'Frau' },
  { label: 'Divers', value: 'Divers' },
];

const formErrors = ref({
  salutation: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  street: '',
  houseNumber: '',
  zipCode: '',
  city: '',
  postalAddress: {
    salutation: '',
    firstName: '',
    lastName: '',
    street: '',
    houseNumber: '',
    zipCode: '',
    city: '',
  },
  termsAccepted: '',
});

const resetErrors = (errorsObj) => {
  for (const key in errorsObj) {
    if (typeof errorsObj[key] === 'object' && errorsObj[key] !== null) {
      resetErrors(errorsObj[key]);
    } else {
      errorsObj[key] = '';
    }
  }
};

const validateField = (fieldPath) => {
  let rules = validationRules[fieldPath];
  if (!rules) return true; // kein Regelwerk

  const parts = fieldPath.split('.');
  let value = parts.reduce((acc, key) => acc?.[key], formData.value);
  let error = '';
  for (const rule of rules) {
    const result = rule(value);
    if (result !== true) {
      error = result;
      break;
    }
  }

  if (parts.length === 1) {
    formErrors.value[fieldPath] = error;
  } else {
    let ref = formErrors.value;
    parts.slice(0, -1).forEach(p => ref = ref[p]);
    ref[parts.at(-1)] = error;
  }

  return error === '';
};



const validateAllFields = () => {
  resetErrors(formErrors.value);
  let isValid = true;

  const mainFields = Object.keys(validationRules).filter(f => !f.startsWith("postalAddress."));
  const postalFields = Object.keys(validationRules).filter(f => f.startsWith("postalAddress."));

  for (const field of mainFields) {
    // **SPEICHERN SIE DAS ERGEBNIS**
    const fieldIsValid = validateField(field);

    // **PRÜFEN SIE DAS ERGEBNIS** (true bei Erfolg, String bei Fehler)
    if (fieldIsValid !== true) {
      isValid = false; // Formular als ungültig markieren
    }
  }

  if (formData.value.differentPostalAddress) {
    for (const field of postalFields) {
      // **SPEICHERN SIE DAS ERGEBNIS**
      const fieldIsValid = validateField(field);

      if (fieldIsValid !== true) {
        isValid = false; // Formular als ungültig markieren
      }
    }
  }

  return isValid;
};

const apiFieldErrors = computed(() => workflowStore.apiFieldErrors);
const globalApiError = computed(() => {
  return workflowStore.workflowContext?.errorMessage || null;
});

// NEUE HILFSFUNKTION FÜR API-FEHLER
const getApiError = (fieldPath) => {
  if (!apiFieldErrors.value) return null;

  // Spezial-Mappings für API-Fehler, die anders benannt sind als Frontend-Felder
  if (fieldPath === 'zipCode') {
    return typeof apiFieldErrors.value.zipCode === 'string' ? apiFieldErrors.value.zipCode : null;
  }
  if (fieldPath === 'phone') {
    return typeof apiFieldErrors.value.phone === 'string' ? apiFieldErrors.value.phone : null;
  }
  return null;
}

const getCombinedFieldError = computed(() => (fieldPath) => {
  const frontendError = fieldPath.includes('.')
      ? fieldPath.split('.').reduce((obj, key) => obj?.[key], formErrors.value)
      : formErrors.value[fieldPath];

  const apiError = getApiError(fieldPath); // API-Fehler vom Helper holen

  return frontendError || apiError; // Gibt den ersten "truthy" Fehler zurück
});

const handleInputChange = (fieldPath) => {
  // Sofortigen lokalen Fehler löschen, damit das Feld sofort "grün" wird
  // Dies löscht nur den Frontend-Validierungsfehler für dieses Feld
  const parts = fieldPath.split('.');
  if (parts.length === 1) {
    formErrors.value[fieldPath] = '';
  } else {
    let ref = formErrors.value;
    parts.slice(0, -1).forEach(p => ref = ref[p]);
    ref[parts.at(-1)] = '';
  }

  // Sende ein Event an XState, um ALLE API-Fehler zu löschen
  workflowStore.sendWorkflowEvent({ type: 'CLEAR_API_ERRORS' });
};
const handleSubmit = async () => {
  const isValid = validateAllFields();

  if (isValid) {
    workflowStore.sendWorkflowEvent({
      type: 'CONTACT_FORM_SUBMITTED',
      data: JSON.parse(JSON.stringify(formData.value)),
    });
  } else {
    await nextTick();
    //window.scrollTo({ top: 0, behavior: 'smooth' });

    // Fokus auf die Statusmeldung im Spinner setzen
    const loadingMessageElement = document.getElementById('loading-message');
    if (loadingMessageElement) {
      loadingMessageElement.focus();
    }
    const firstErrorField = document.querySelector('.error-message');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const inputElement = firstErrorField.previousElementSibling;
      if (inputElement && (inputElement.tagName === 'INPUT' || inputElement.tagName === 'TEXTAREA' || inputElement.tagName === 'SELECT')) {
        inputElement.focus();
      } else {
        const labelElement = firstErrorField.closest('.form-group')?.querySelector('label');
        if (labelElement) labelElement.focus();
      }
    }
  }
};

watch(() => formData.value.differentPostalAddress, (newValue) => {
  if (!newValue) {
    formData.value.postalAddress = {
      salutation: '',
      firstName: '',
      lastName: '',
      street: '',
      houseNumber: '',
      zipCode: '',
      city: '',
    };
    formErrors.value.postalAddress = {
      salutation: '',
      firstName: '',
      lastName: '',
      street: '',
      houseNumber: '',
      zipCode: '',
      city: '',
    };
  }
});


watch(() => workflowStore.workflowContext.formData, (newFormData) => {
  if (newFormData?.contactForm) {
    const cf = newFormData.contactForm;

    formData.value = {
      salutation: cf.salutation || '',
      firstName: cf.firstName || '',
      lastName: cf.lastName || '',
      email: cf.email || '',
      phone: cf.phone || '',
      street: cf.street || '',
      houseNumber: cf.houseNumber || '',
      zipCode: cf.zipCode || '',
      city: cf.city || '',
      differentPostalAddress: cf.differentPostalAddress || false,
      postalAddress: {
        salutation: cf.postalAddress?.salutation || '',
        firstName: cf.postalAddress?.firstName || '',
        lastName: cf.postalAddress?.lastName || '',
        street: cf.postalAddress?.street || '',
        houseNumber: cf.postalAddress?.houseNumber || '',
        zipCode: cf.postalAddress?.zipCode || '',
        city: cf.postalAddress?.city || '',
      },
      termsAccepted: cf.termsAccepted || false,
    };
  }
}, { immediate: true });


onMounted(async () => {
  optinTextVES.value = '';
  if (showOptinVES.value  && optinTextVESUrl.value) {
    try {
      const response = await fetch(optinTextVESUrl.value);
      if (response.ok) {
        optinTextVES.value = await response.text();
      } else {
        optinTextVES.value = '';
        console.error('Fehler beim Laden des Opt-In-Texts:', response.statusText);
      }
    } catch (error) {
      optinTextVES.value = '';
      console.error('Netzwerkfehler beim Laden des Opt-In-Texts:', error);
    }
  }
});

</script>
<style scoped>


#app-ves-clickstream .contact-form fieldset {
  border: none;
  padding: 0;
  margin: 0 0 16px 0;
}

#app-ves-clickstream .contact-form-headline {
  text-align: center;
  margin: 0;
}

#app-ves-clickstream .contact-form-headline h3 {
  margin-top: 0;
  margin-bottom: 5px;
}

#app-ves-clickstream .contact-form-headline span {
  display: block;
  margin-bottom: 0; /* Kein zusätzlicher Abstand hier */
}


#app-ves-clickstream .contact-form {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 20px;
  /*padding: 2em;*/
  /*padding: 32px 16px;*/
  /*
  padding: 8px;
  padding-bottom: 16px;*/
  padding: 48px 64px;
  border: 1px solid #ccc;
  /*border-radius: 8px;*/
  background-color: var(--input-background-color);
}


/* WICHTIGE ÄNDERUNG: Position: relative für den Wrapper, um Labels absolut darin zu positionieren */
#app-ves-clickstream .form-group {
  /* ABSTAND SOLL 32PX REMINDER FOR ROLLBACK
  margin-bottom: 1.5em;
  margin-top: 1.5em;
   */
  margin-bottom: 32px;
  margin-top: 32px;



  position: relative; /* WICHTIG */
  /*margin-bottom: 40px;*/
}

#app-ves-clickstream .contact-form form > .form-group:first-child {
  margin-top: 0;
}

/* Die folgenden Regeln beziehen sich nur auf die Standard-Eingabefelder (Text, Email, Tel),
   NICHT auf Radio-Gruppen oder Checkboxen. */
#app-ves-clickstream .form-group label.form-label-notch {
  all: unset;
  position: absolute !important;
  /*
  top: -10px;
  left: 12px;
  font-size: 1em;
   */
  top: -7px;
  left: 7px;
  font-size: 12px;
  line-height: 1;
  padding: 0 6px;
  background: var(--input-background-color, #fff); /* Nutzt die Hintergrundfarbe der Komponente */
  color: var(--primary-text-color);
  pointer-events: none;
  display: inline-block !important;
  margin-bottom: 0 !important;
  font-weight: normal !important;
  z-index: 2; /* Stellt sicher, dass das Label über dem Input-Rand liegt */
  font-family: inherit;
}

/* Anpassungen für INPUT-FELDER, um Platz für das "Notch"-Label zu schaffen */
#app-ves-clickstream .form-group input[type="text"],
#app-ves-clickstream .form-group input[type="email"],
#app-ves-clickstream .form-group input[type="tel"] {
  width: 100%;
  /*
  padding: 1.2em 0.8em 0.8em 0.8em;
  border: 1px solid black;
   */
  padding: 12px;
  border: 1px solid #4D4D4D;
  border-radius: 0;
  box-sizing: border-box;
}

#app-ves-clickstream .form-group input:not([type="radio"]):not([type="checkbox"]):focus {
  outline:2px solid var(--outline-color);
  outline-offset:.4em;
}

#app-ves-clickstream .form-group input[type="radio"]:focus,
#app-ves-clickstream .form-group input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}


/* --- Radio- & Checkbox-Spezifisches Styling (unverändert gelassen) --- */

#app-ves-clickstream .radio-options label {
  display: inline-flex;
  align-items: center;
  margin-right: 16px;
  font-weight: normal;
}


#app-ves-clickstream .required {
  color: var(--error-text-color);
  margin-left: 0.25rem;
}

#app-ves-clickstream .form-group input[aria-invalid="true"],
#app-ves-clickstream .form-group[aria-invalid="true"] {
  border-color: var(--error-text-color);
  border: 2px solid var(--error-text-color);
  box-shadow: none;
}

#app-ves-clickstream .error-message {
  color: var(--error-text-color);
  font-size: 1em;
  margin-top: 0.4em;
}

#app-ves-clickstream .checkbox-group {
  display: flex;
  align-items: flex-start;

  margin-top: 1.1em;
  margin-bottom: 1.1em;

  margin-left: 0;
  margin-right: 0;
/*
  margin: 15px 8px;
*/
}

#app-ves-clickstream .checkbox-group input[type="checkbox"] {

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  font: inherit;

  box-sizing: border-box;
  width: 24px;
  height: 24px;
  padding: 0;

  border: 1px solid #4D4D4D;
  border-radius: 0;
  background-color: white;

  position: relative;
  margin-right: 0px;
  flex-shrink: 0;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;

  margin-top: -3.25px;
}

/* Häkchen */
#app-ves-clickstream .checkbox-group input[type="checkbox"]::before {
  content: '';
  display: block;
  position: absolute;
  top: 45%;
  left: 50%;

  width: 6px;
  height: 11px;
  border-right: 2px solid #4D4D4D;
  border-bottom: 2px solid #4D4D4D;
  transform: translate(-50%, -50%) rotate(45deg);

  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}

#app-ves-clickstream .checkbox-group input[type="checkbox"]:checked::before {
  opacity: 1;
}

#app-ves-clickstream .checkbox-group label {
  font-weight: normal;
  display: inline;
  padding-left: 12px;
  line-height: 125%;
}
/* ... Rest des CSS (terms-checkbox, different-address-section, form-row, loading-overlay) ... */

#app-ves-clickstream .terms-checkbox :deep(a) {
  color: #004080;
  text-decoration: underline;
}

#app-ves-clickstream .terms-checkbox :deep(a:hover) {
  color: #002050;
  text-decoration: underline;
}

#app-ves-clickstream .terms-checkbox :deep(strong) {
  font-size: 100%;
  font-weight: 100;
}

#app-ves-clickstream .ves-optin :deep(strong) {
  font-size: 100%;
  font-weight: 100;
}

#app-ves-clickstream .different-address-section {
  border: 1px dashed var(--outline-color);
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1.5rem;
  background-color: var(--input-background-color);
}



#app-ves-clickstream .form-row {
  display: flex;
  gap: 32px 16px;
  flex-wrap: wrap;
  margin-top: 32px;
}

#app-ves-clickstream .form-row .form-group {
  margin-bottom: 0;
  margin-top: 0;
  flex-grow: 1;
  position: relative; /* Hinzugefügt für form-row gruppen */
}
@media (min-width: 768px) {

  .form-row > .form-group {
    flex: 1 1 0; /* Genau 50% Breite für jedes Feld */
    min-width: 0;
  }
}


#app-ves-clickstream .form-row .street-group,
#app-ves-clickstream .form-row .house-number-group,
#app-ves-clickstream .form-row .zip-code-group,
#app-ves-clickstream .form-row .city-group {
  flex-basis: 100%;
  max-width: 100%;
}

#app-ves-clickstream .radio-options {
  margin-top: 0.3em;
}

#app-ves-clickstream .radio-options input[type="radio"] {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  box-sizing: border-box;
  width: 24px;
  height: 24px;
  padding: 0;

  /* Styling des äußeren weißen Kreises */
  border: 1px solid #4D4D4D;
  border-radius: 50%;
  background-color: white;

  /* Stellt sicher, dass der innere Punkt positioniert werden kann */
  position: relative;

  /* Übergang für sanfte Farbwechsel des inneren Punkts */
  transition: all 0.15s ease-in-out;

  margin-right: 8px;
  flex-shrink: 0;
  cursor: pointer; /* Zeigt an, dass es anklickbar ist */
}

/* PSEUDOELEMENT: Der innere schwarze Punkt */
#app-ves-clickstream .radio-options input[type="radio"]::before {
  content: ''; /* Notwendig für Pseudoelemente */
  display: block;

  /* Größe und Position des inneren Punkts */
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #000;

  /* Zentrierung des inneren Punkts */
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  /* Standardmäßig unsichtbar */
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}

/* Zustand: Radiobox ist gecheckt - Zeigt den schwarzen Punkt an */
#app-ves-clickstream .radio-options input[type="radio"]:checked::before {
  opacity: 1; /* Macht den inneren Punkt sichtbar */
}

/* Zusätzliches Styling für den Fokus-Ring (Barrierefreiheit) */
#app-ves-clickstream .form-group input[type="radio"]:focus {
  outline: 2px solid var(--outline-color); /* Behält den blauen Fokus-Ring */
  outline-offset: 2px;
  box-shadow: none;
}

@media (max-width: 767px) {
  /* Fügt eine neue Regel hinzu, um Radio-Elemente untereinander anzuzeigen */
  #app-ves-clickstream .radio-options {
    display: flex;
    margin-top: 0.5em;
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: 16px;
    row-gap: 8px;
  }

  #app-ves-clickstream .radio-options label {
    display: flex; /* Stellt sicher, dass das Label und der Radio-Button nebeneinander bleiben */
    margin-right: 0; /* Entfernt den rechten Abstand zwischen den Optionen */
    margin-bottom: 0;
  }

  #app-ves-clickstream .contact-form {
    padding: 32px 16px;
  }

  #app-ves-clickstream [aria-labelledby="salutation-label"] .radio-options input[type="radio"] {
    margin-right: 8px;
  }

  #app-ves-clickstream .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 24px;
  }

  #app-ves-clickstream .form-actions .button_yellow {
    display: block;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
  }

  #app-ves-clickstream .form-actions .button_link_back {
    align-self: center;
    text-align: center;
    padding: 0;
  }
}

@media (min-width: 768px) {
  #app-ves-clickstream .form-row {
    align-items: flex-start;
  }
  #app-ves-clickstream .form-row .street-group {
    flex-basis: 75%;

  }

  #app-ves-clickstream .form-row .house-number-group {
    flex-basis: 21%;
    max-width: 22%;
  }


  #app-ves-clickstream .form-row .zip-code-group {
    flex-basis: 21%;
    max-width: 22%;
  }

  #app-ves-clickstream .form-row .city-group {
    flex-basis: 75%;
    max-width: 75%;
  }


}

#app-ves-clickstream .form-loading-overlay {
  /*position: absolute;
  top: 0 */
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9); /* Halbdurchsichtiger Hintergrund */
  display: flex;
  flex-direction: column; /* Für Text unter dem Spinner */
  justify-content: center;
  align-items: center;
  z-index: 10; /* Über dem Formular, unter globalen Overlays */
  border-radius: 8px; /* Passend zum Formular */
  text-align: center;
  position: fixed;
  inset: 0;
}

#app-ves-clickstream .spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

#app-ves-clickstream .spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--outline-color);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem; /* Abstand zum Text */
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Formular leicht deaktivieren, wenn es lädt */
#app-ves-clickstream form.is-loading {
  pointer-events: none; /* Deaktiviert Klicks und Eingaben */
  opacity: 0.6; /* Macht es leicht durchscheinend */
}

#app-ves-clickstream .global-error-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background-color: #fff3f3; /* Sanftes Rot */
  border: 1px solid var(--error-text-color, #d32f2f);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5em;
}

#app-ves-clickstream .contact-form-headline {
  text-align: center;
  margin: 0;
  margin-bottom: 30px; /* Weist dem Text einen klaren Abstand zum Formular zu */
}

#app-ves-clickstream hr {
  all: unset;
  display: block;
  box-sizing: border-box;
  border: none;

  /* Trick für messerscharfe 1px-Linien auf jedem Bildschirm: */
  height: 1px !important;

  background-color: transparent;
  border-top: 1px solid var(--color-grey-30);


  /* Abstände */
  /*
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  margin-left: 1.0em;
  margin-right: 1.0em;
   */
  margin: 32px 0px;
}


#app-ves-clickstream .form-actions .button_yellow {
  display: inline-block;
  margin-left: 0;
  margin-top: 0;
}

#app-ves-clickstream .button_link_back {
  all: unset; /* Setzt alle Standard-Button-Eigenschaften zurück */
  font-family: inherit;
  font-size: 14px;
  color: #004080;
  text-decoration: underline;
  cursor: pointer;
  padding: 0.4em 0.8em;
  border-radius: 4px;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

/* Hover- & Focus-Zustände */
#app-ves-clickstream .button_link_back:hover {
  color: #002050;
}

#app-ves-clickstream .button_link_back:focus-visible {
  outline: 2px solid var(--outline-color, #004080);
  outline-offset: 2px;
}


#app-ves-clickstream .form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end; /* Richtet die Buttons rechtsbündig aus */
  gap: 1.5rem; /* Abstand zwischen Zurück-Link und Absenden-Button */
  margin-top: 1.5em;
}

#app-ves-clickstream hr + .form-actions {
  margin-top: 0;
}

</style>